import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { Printer, FileSpreadsheet, FileText, Layers, Package, Hash, RefreshCw, Loader2, Trash2, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="glass rounded-xl p-4 flex items-center gap-4">
    <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${accent}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="font-mono-tech text-2xl sm:text-3xl font-extrabold text-slate-100 tabular-nums">{value}</div>
      <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
    </div>
  </div>
);

export default function ProduzioneDTF() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [tab, setTab] = useState("Da stampare");
  const [selected, setSelected] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const rowKey = (r) => `${r.id_grafica ?? ""}|${r.sku_neutro}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [agg, st] = await Promise.all([api.get("/dtf/aggregato", { params: { stato: tab } }), api.get("/stats")]);
      setRows(agg.data);
      setStats(st.data);
      setSelected({});
    } catch (e) {
      toast.error("Errore nel caricamento dei dati di produzione");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const advance = async (r, a_stato) => {
    try {
      await api.post("/dtf/avanza-lotto", { id_grafica: r.id_grafica, sku_neutro: r.sku_neutro, da_stato: tab, a_stato });
      toast.success(`Lotto ${r.id_grafica || ""} → ${a_stato}`);
      load();
    } catch (e) {
      toast.error("Errore nell'aggiornamento dello stato");
    }
  };

  const exportFile = async (formato) => {
    setExporting(true);
    try {
      const lotti = rows.filter((r) => selected[rowKey(r)]).map((r) => ({ id_grafica: r.id_grafica ?? null, sku_neutro: r.sku_neutro }));
      const res = await api.post("/dtf/export", { formato, stato: tab, lotti }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = formato === "csv" ? "produzione_dtf.csv" : "produzione_dtf.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(lotti.length ? `${lotti.length} lotti esportati in ${formato.toUpperCase()}` : `Lista completa esportata in ${formato.toUpperCase()}`);
    } catch (e) {
      toast.error("Errore durante l'esportazione");
    } finally {
      setExporting(false);
    }
  };

  const selectedKeys = Object.keys(selected).filter((k) => selected[k]);
  const toggleRow = (r) => setSelected((p) => ({ ...p, [rowKey(r)]: !p[rowKey(r)] }));
  const toggleAll = () => {
    if (selectedKeys.length === rows.length) return setSelected({});
    const all = {}; rows.forEach((r) => (all[rowKey(r)] = true)); setSelected(all);
  };
  const avviaBulk = async () => {
    const lotti = rows.filter((r) => selected[rowKey(r)]).map((r) => ({ id_grafica: r.id_grafica ?? null, sku_neutro: r.sku_neutro }));
    if (!lotti.length) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post("/dtf/avanza-bulk", { lotti, da_stato: "Da stampare", a_stato: "In produzione" });
      toast.success(`${data.aggiornati} ordini avviati in produzione`);
      load();
    } catch (e) {
      toast.error("Errore nell'avvio produzione in bulk");
    } finally {
      setBulkLoading(false);
    }
  };

  const eliminaSelezionati = async () => {
    const lotti = rows.filter((r) => selected[rowKey(r)]).map((r) => ({ id_grafica: r.id_grafica ?? null, sku_neutro: r.sku_neutro }));
    if (!lotti.length) return;
    if (!window.confirm(`Eliminare definitivamente ${lotti.length} lotti dalla lista «${tab}»? L'operazione è irreversibile.`)) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post("/dtf/elimina-bulk", { lotti, stato: tab });
      toast.success(`${data.eliminati} ordini eliminati`);
      load();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    } finally {
      setBulkLoading(false);
    }
  };

  const eliminaLotto = async (r) => {
    if (!window.confirm(`Eliminare il lotto ${r.id_grafica || ""} (${r.sku_neutro})? L'operazione è irreversibile.`)) return;
    try {
      const { data } = await api.post("/dtf/elimina-bulk", { lotti: [{ id_grafica: r.id_grafica ?? null, sku_neutro: r.sku_neutro }], stato: tab });
      toast.success(`${data.eliminati} ordini eliminati`);
      load();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-cyan-400 text-xs uppercase tracking-widest mb-1">
            <Printer className="w-4 h-4" /> Coda di stampa
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-100 tracking-tight">Produzione DTF</h1>
          <p className="text-slate-400 text-sm mt-1">Ordini «Da stampare» aggregati per Grafica e SKU Neutro</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="refresh-btn" onClick={load} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-700/40 text-sm">
            <RefreshCw className="w-4 h-4" /> Aggiorna
          </button>
          <button data-testid="export-excel-btn" onClick={() => exportFile("xlsx")} disabled={exporting}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-500 hover:bg-green-400 text-slate-900 font-semibold text-sm disabled:opacity-60">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Esporta in Excel
          </button>
          <button data-testid="export-csv-btn" onClick={() => exportFile("csv")} disabled={exporting}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 font-semibold text-sm disabled:opacity-60">
            <FileText className="w-4 h-4" /> Esporta CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard icon={Package} label="Capi da stampare" value={stats.capi_da_stampare ?? "—"} accent="bg-cyan-400/15 text-cyan-400" />
        <StatCard icon={Hash} label="Grafiche uniche" value={stats.grafiche_uniche ?? "—"} accent="bg-amber-400/15 text-amber-400" />
        <StatCard icon={Layers} label="Lotti da produrre" value={rows.length} accent="bg-green-500/15 text-green-400" />
      </div>

      <div className="flex gap-1.5 mb-4 overflow-x-auto" data-testid="dtf-tabs">
        {["Da stampare", "In produzione", "Pronto"].map((t) => (
          <button key={t} data-testid={`dtf-tab-${t.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${tab === t ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/40" : "border border-slate-700 text-slate-400 hover:bg-slate-700/40"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Da stampare" && selectedKeys.length > 0 && (
        <div className="mb-3 flex items-center gap-3 bg-amber-400/10 border border-amber-400/30 rounded-lg px-4 py-2.5" data-testid="bulk-bar">
          <span className="text-sm text-amber-200">{selectedKeys.length} lotti selezionati</span>
          <div className="ml-auto flex flex-wrap gap-2">
            {isAdmin && (
              <button data-testid="bulk-elimina-btn" onClick={eliminaSelezionati} disabled={bulkLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-300 border border-red-500/40 hover:bg-red-500/30 font-semibold text-sm disabled:opacity-60">
                <Trash2 className="w-4 h-4" /> Elimina selezionati
              </button>
            )}
            <button data-testid="bulk-avvia-btn" onClick={avviaBulk} disabled={bulkLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold text-sm disabled:opacity-60">
              {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Avvia produzione selezionati
            </button>
          </div>
        </div>
      )}

      <div className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="dtf-table">
            <thead>
              <tr className="border-b border-slate-700/60">
                {tab === "Da stampare" && (
                  <th className="px-4 py-3 w-10">
                    <input type="checkbox" data-testid="dtf-select-all" checked={rows.length > 0 && selectedKeys.length === rows.length} onChange={toggleAll} className="accent-cyan-400 w-4 h-4" />
                  </th>
                )}
                {["ID Grafica", "SKU Neutro", "Descrizione Capo", "Data Ordine", "Quantità Totale", "N. Ordini", "Azioni"].map((h) => (
                  <th key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-400 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={tab === "Da stampare" ? 8 : 7} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={tab === "Da stampare" ? 8 : 7} className="px-4 py-12 text-center text-slate-500">Nessun lotto in stato «{tab}».</td></tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={`${r.id_grafica}-${r.sku_neutro}`} data-testid={`dtf-row-${i}`} className="border-b border-slate-800/60 hover:bg-slate-700/20 transition-colors">
                    {tab === "Da stampare" && (
                      <td className="px-4 py-3">
                        <input type="checkbox" data-testid={`dtf-select-${i}`} checked={!!selected[rowKey(r)]} onChange={() => toggleRow(r)} className="accent-cyan-400 w-4 h-4" />
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span className="font-mono-tech text-cyan-300 bg-cyan-400/10 px-2 py-0.5 rounded text-sm">{r.id_grafica || "—"}</span>
                    </td>
                    <td className="px-4 py-3 font-mono-tech text-slate-300 text-sm">{r.sku_neutro}</td>
                    <td className="px-4 py-3 text-slate-300 text-sm">
                      {r.descrizione_capo}
                      {r.personalizzata && <span className="ml-2 text-[10px] uppercase tracking-wider bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30 rounded px-1.5 py-0.5">Personalizzata</span>}
                      {(r.immagini || []).map((u, k) => (
                        <a key={k} href={u} target="_blank" rel="noreferrer" data-testid={`img-link-${i}-${k}`} className="mt-1 flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 underline">
                          <ExternalLink className="w-3 h-3" /> Immagine{r.immagini.length > 1 ? ` ${k + 1}` : ""}
                        </a>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-sm whitespace-nowrap">{r.data_da}{r.data_a && r.data_a !== r.data_da ? ` – ${r.data_a}` : ""}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono-tech text-lg font-bold text-slate-100">{r.quantita_totale}</span>
                      <span className="text-slate-500 text-xs ml-1">pz</span>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-sm">{r.num_ordini}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5 flex-wrap">
                        {tab === "Da stampare" && (
                          <button data-testid={`avvia-${i}`} onClick={() => advance(r, "In produzione")} className="px-2.5 py-1.5 rounded-md bg-amber-400/15 text-amber-300 hover:bg-amber-400/25 text-xs font-semibold">Avvia produzione</button>
                        )}
                        {tab === "In produzione" && (
                          <>
                            <button data-testid={`pronto-${i}`} onClick={() => advance(r, "Pronto")} className="px-2.5 py-1.5 rounded-md bg-green-500/15 text-green-300 hover:bg-green-500/25 text-xs font-semibold">Segna Pronto</button>
                            <button onClick={() => advance(r, "Da stampare")} className="px-2.5 py-1.5 rounded-md bg-slate-700/40 text-slate-300 hover:bg-slate-600/60 text-xs">In coda</button>
                          </>
                        )}
                        {tab === "Pronto" && (
                          <button onClick={() => advance(r, "In produzione")} className="px-2.5 py-1.5 rounded-md bg-slate-700/40 text-slate-300 hover:bg-slate-600/60 text-xs">Riapri</button>
                        )}
                        {isAdmin && (
                          <button data-testid={`elimina-${i}`} onClick={() => eliminaLotto(r)} className="px-2.5 py-1.5 rounded-md bg-red-500/15 text-red-300 hover:bg-red-500/25 text-xs font-semibold">Elimina</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
